import { TERMINAL, TRANSITIONS, type Stage, type TransitionRule } from '@velaigal/shared';

/*
 * WHICH DECISION BUTTONS THE ACTION BAR MAY SHOW, read out of the machine itself.
 *
 * This is the FIRST value import from @velaigal/shared anywhere in the web app — everything
 * before it (src/lib/api.ts's ErrorCode) was type-only and erased at build time. That has one
 * consequence worth stating where someone will find it: `npm run build` now genuinely needs
 * packages/shared/dist, which is gitignored, so the root `build` script builds it first
 * (package.json). CI already ran `npm run build:shared` as its own step and the deploy Dockerfile
 * already built it before `npm run build`; the script change is what makes a fresh clone work
 * without either.
 *
 * Paying that price buys the thing a second hardcoded list cannot: there is no offerable-set
 * table to drift. Before this module the bar decided visibility with
 * `stage !== 'shortlisted' && stage !== 'rejected'`, which offered Shortlist out of `rejected`
 * (a permanent dead end — TRANSITIONS.rejected has one edge and it is not that) and offered both
 * buttons on `hired` and `withdrawn`, where the recruiter has no edges at all. Every one of those
 * clicks came back 409 invalid_transition.
 *
 * WHAT IS DELIBERATELY *NOT* DERIVED: which moves the bar implements at all. TRANSITIONS also
 * carries `-> interviewing`, `-> hired` and `-> withdrawn` edges; this screen has no UI for them,
 * and inventing buttons for edges nobody designed copy for is not the same problem as hiding
 * buttons the server refuses. ORDER below is therefore the bar's own inventory, and the graph
 * decides which of those three are legal from a given stage.
 */

/** The three moves the applicant screen's action bar knows how to send. */
export type OfferedAction = 'shortlist' | 'reject' | 'reopen';

export interface DecisionOffer {
  action: OfferedAction;
  toStage: Stage;
  /** Straight off TRANSITIONS' rule. True on every recruiter edge today — a member views and
   *  annotates, and does not decide — so the bar disables rather than hides, and says why. */
  requiresOwner: boolean;
  requiresRationale: boolean;
}

const TARGET: Readonly<Record<OfferedAction, Stage>> = {
  shortlist: 'shortlisted',
  reject: 'rejected',
  reopen: 'under_review',
};

/** Render order, and the bar's whole inventory. Not a claim about legality. */
const ORDER: readonly OfferedAction[] = ['shortlist', 'reject', 'reopen'];

/**
 * The moves offerable from `stage`: exactly the bar's inventory intersected with the recruiter
 * edges TRANSITIONS actually contains from there.
 *
 * `stage` is typed `string` because it arrives off the wire as one. An unknown value yields no
 * offers rather than throwing — a screen with no buttons is recoverable, a crashed one is not.
 */
export function offeredDecisions(stage: string): DecisionOffer[] {
  const from = stage as Stage;
  const edges: Readonly<Partial<Record<Stage, TransitionRule>>> | undefined = TRANSITIONS[from];
  if (!edges) return [];
  return ORDER.flatMap((action): DecisionOffer[] => {
    const toStage = TARGET[action];
    const rule = edges[toStage];
    if (!rule) return [];
    // `hired -> under_review` is an ADMIN edge (stages.ts). A recruiter pressing it earns
    // 409 invalid_transition however owned they are, so it is not offerable here at all.
    if (!rule.actorKinds.includes('recruiter')) return [];
    // `under_review` is the target of TWO different actions: `start_review` out of `submitted`,
    // and `reopen` out of an ending. Only the second is this bar's `reopen`, and the machine's
    // own TERMINAL set is what tells them apart — no second stage list here.
    if (toStage === 'under_review' && !TERMINAL.has(from)) return [];
    return [{
      action,
      toStage,
      requiresOwner: rule.requiresOwner,
      requiresRationale: rule.requiresRationale,
    }];
  });
}

/**
 * Whether this move needs a written reason before the button may be pressed.
 *
 * The UNION of the two server rules, mirrored rather than picked between, exactly as
 * server/src/review/decide.ts enforces it: the EDGE's own requiresRationale (stricter on the
 * reopen edges), and decisions_rationale_check (stricter whenever the stage is terminal or the
 * reason code is 'other', which is the code a reopen sends). Mirroring the union is what makes
 * the disabled button honest — a blank rationale would be a 400 the recruiter never asked for.
 */
export function requiresWrittenReason(offer: DecisionOffer, reasonCode: string): boolean {
  return offer.requiresRationale
    || offer.toStage === 'hired'
    || offer.toStage === 'rejected'
    || reasonCode === 'other';
}
